import type { FeatureFlagKey, SkillData } from '@sb/feathers-types';
import type { EquipmentItem } from '@sb/integrations/types';
import { makeNamespacedLog } from '@sb/log';
import type {
  EnvironmentVariable,
  Robot,
  Routine,
} from '@sb/remote-control/types';
import type { Space } from '@sb/routine-runner';
import { precheckForExecution } from '@sb/routine-validation';
import type { RobotOperationMode } from '@sb/service-interfaces/host/types';

import type { SubroutineContext } from '../../hooks/useSubroutineContext';

const log = makeNamespacedLog('remote-control.isRoutinePlayable');

/**
 * Failure signatures already warned about, per routine id.
 * `isRoutinePlayable` runs during ordinary React renders (play buttons,
 * warning widgets, previews), often from several simultaneously-mounted
 * callers whose inputs (equipment, feature flags, subroutine context) can
 * produce different failure sets for the same routine. Tracking every
 * signature seen — not just the last one — keeps concurrent callers from
 * evicting each other's entry and re-warning on every render. Entries
 * reset once the routine is playable again.
 */
const warnedFailureSignatures = new Map<string, Set<string>>();

const failureSignature = (errors: unknown): string => {
  try {
    return JSON.stringify(errors) ?? 'unserializable';
  } catch {
    return 'unserializable';
  }
};

interface IsRoutinePlayableArgs {
  routine: Routine.ConvertedResponse;
  equipment: EquipmentItem[];
  globalSpace: Space.Item[];
  globalVariables: EnvironmentVariable[];
  ioOutputs: Robot.IOPort[];
  isROS2Enabled?: boolean;
  featureFlags?: Partial<Record<FeatureFlagKey, boolean>>;
  subroutineCtx?: SubroutineContext;
  skills?: SkillData[];
  robotOperationMode?: RobotOperationMode;
}

export function isRoutinePlayable({
  routine,
  equipment,
  globalSpace,
  globalVariables,
  ioOutputs,
  isROS2Enabled = false,
  featureFlags = {},
  subroutineCtx,
  skills,
  robotOperationMode,
}: IsRoutinePlayableArgs): boolean {
  // Equipment-reference checks (missing/disabled welder, torch, gripper) are
  // part of the rule engine now (see `steps/Weld` and `steps/ActuateGripper`),
  // so they run through `precheckForExecution` and — unlike the old standalone
  // `findEquipmentRefDiagnostics` scan — correctly skip effectively-suppressed
  // steps. The standalone scan remains only for per-step editor display and the
  // import sanitizer.
  const result = precheckForExecution(routine, {
    ctx: {
      equipment,
      globalSpace,
      globalVariables,
      ioOutputs,
      isROS2Enabled,
      featureFlags,
      skills,
      robotOperationMode,
      ...subroutineCtx,
    },
  });

  if (!result.ok) {
    const signature = failureSignature(result.errors);
    const warned = warnedFailureSignatures.get(routine.id) ?? new Set<string>();

    if (!warned.has(signature)) {
      warned.add(signature);
      warnedFailureSignatures.set(routine.id, warned);

      log.warn('precheckFailed', 'Routine precheck failed — Play disabled', {
        routineID: routine.id,
        routineName: routine.name,
        errors: result.errors,
      });
    }

    return false;
  }

  warnedFailureSignatures.delete(routine.id);

  return true;
}
